import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgForm } from '@angular/forms';
import { delay } from 'rxjs/operators';

import {
    ActivityInterface,
    CountryInterface,
    GetActivitiesUseCase,
    GetCountriesUseCase,
    GetProfessionalPositionsUseCase,
    ProfessionalPositionInterface,
    TransferInterface,
} from '../../../../core';
import { BaseUI } from '../../base.ui';
import { Document, FullClient } from '../../../domain/data';
import { ErrorDialog } from '../../dialog/error/error.dialog';
import { ParentEvents } from '../../../utils/parent-events.service';
import { ParentFormDirective } from '../../../directives/parent-form.directive';

@Component({
    selector: 'thr-full-data-nine-natural-person',
    templateUrl: './full-data-nine-natural-person.component.html',
    styleUrls: ['./full-data-nine-natural-person.component.scss'],
})
export class FullDataNineNaturalPersonComponent extends BaseUI implements AfterViewInit, OnDestroy, OnInit {
    private activities: ActivityInterface[] = [];
    private countries: CountryInterface[] = [];
    private professionalPositions: ProfessionalPositionInterface[] = [];

    public form: NgForm;
    public isFormValid = false;

    @Input()
    client: FullClient;

    @Input()
    transfer: TransferInterface;

    @Input()
    isDisabled = false;

    @Input()
    isDisabledSubmit = false;

    @Output()
    documentView: EventEmitter<Document> = new EventEmitter();

    @ViewChild('formDirective')
    formDirective: ParentFormDirective;

    constructor(
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private getActivitiesUseCase: GetActivitiesUseCase,
        private getCountriesUseCase: GetCountriesUseCase,
        private getProfessionalPositionsUseCase: GetProfessionalPositionsUseCase
    ) {
        super(parentEvents);
    }

    get hasRisk(): boolean {
        return this.hasProfessionalRisk || this.hasCountryRisk || this.hasPublicFunctions || this.isInvestmentHigh;
    }

    get hasProfessionalRisk(): boolean {
        if (!!this.activities.length && !!this.professionalPositions.length) {
            if (this.client.isLegalPerson) {
                return this.activities.find((it) => String(it.id) === this.client.activityCode)?.risk || false;
            }

            return this.professionalPositions.find((it) => String(it.id) === this.client.professional.position)?.risk || false;
        }

        return false;
    }

    get hasCountryRisk(): boolean {
        if (!!this.countries.length) {
            return this.countries.find((it) => String(it.id) === this.client.habitualResidence?.country)?.risk || false;
        }

        return false;
    }

    get hasPublicFunctions(): boolean {
        return this.client.publicDuties.isImportant || this.client.publicDuties.isImportantRelative;
    }

    get isInvestmentHigh(): boolean {
        return this.transfer?.total >= 1000000;
    }

    get maxSize(): number {
        return 25 * 1024 * 1024;
    }

    get allowedTypes(): string[] {
        return [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'application/pdf',
            'application/zip',
            'application/octet-stream',
            'application/x-zip-compressed',
            'multipart/x-zip',
        ];
    }

    get allowedExtension(): string[] {
        return ['.jpeg', '.jpg', '.png', '.pdf', '.zip'];
    }

    ngOnInit() {
        this.getActivities();
        this.getCountries();
        this.getProfessionalPositions();
    }

    private getActivities() {
        this.getActivitiesUseCase
            .bind()
            .pipe(delay(1)) // prevent ExpressionChangedAfterItHasBeenCheckedError
            .subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.activities = transaction.data;
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog);
                }
            });
    }

    private getCountries() {
        this.getCountriesUseCase
            .bind()
            .pipe(delay(1)) // prevent ExpressionChangedAfterItHasBeenCheckedError
            .subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.countries = transaction.data;
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog);
                }
            });
    }

    private getProfessionalPositions() {
        this.getProfessionalPositionsUseCase
            .bind()
            .pipe(delay(1)) // prevent ExpressionChangedAfterItHasBeenCheckedError
            .subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.professionalPositions = transaction.data;
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog);
                }
            });
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.form = this.formDirective.controlContainer as NgForm;

        if (this.form) {
            this.form.statusChanges.subscribe(() => {
                setTimeout(() => {
                    this.isFormValid = this.form.valid;
                });
            });
        }
    }

    onDocumentView(document: Document) {
        if (document.id) {
            this.documentView.emit(document);
        }
    }

    onDocumentRemove(document: Document) {
        if (document.id) {
            document.remove = true;
        }
    }
}
